import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { PaginationComponent } from '@appkit4/angular-components/pagination';

@Component({
    selector: 'gtw-pagination-wrapper',
    templateUrl: './pagination-wrapper.component.html',
    styleUrls: ['./pagination-wrapper.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PaginationWrapperComponent implements OnChanges {
  @ViewChild('paginationRef') paginationRef!: PaginationComponent;

  /** Total number of items in the dataset */
  @Input() totalItems: number = 0;

  /** Available page size options */
  @Input() pageSizeOptions: number[] = [5, 8, 12];

  /** Currently selected page size */
  @Input() pageSize: number = 8;

  /** Current page number (1-based) */
  @Input() currentPage: number = 1;

  /** Emits when page size changes */
  @Output() pageSizeChange = new EventEmitter<number>();

  /** Emits when current page changes */
  @Output() currentPageChange = new EventEmitter<number>();

  /** Emits pagination state for parent to slice data */
  @Output() paginationChange = new EventEmitter<{ startIndex: number; endIndex: number; pageSize: number; currentPage: number }>();

  /** Dropdown list formatted for ap-dropdown */
  pageSizeDropdownList: { id: string; value: number; label: string; selected: boolean }[] = [];

  /** Currently selected item in dropdown */
  selectedPageSizeItem: { id: string; value: number; label: string; selected: boolean } | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageSizeOptions'] || changes['pageSize']) {
      this.initDropdownList();
    }
    if (changes['totalItems'] || changes['pageSize'] || changes['currentPage']) {
      this.validateCurrentPage();
    }
  }

  ngOnInit(): void {
    this.initDropdownList();
    this.emitPaginationState();
  }

  private initDropdownList(): void {
    this.pageSizeDropdownList = this.pageSizeOptions.map((size, index) => ({
      id: String(index),
      value: size,
      label: `${size} / Page`,
      selected: size === this.pageSize
    }));

    this.selectedPageSizeItem = this.pageSizeDropdownList.find(item => item.selected) 
      || this.pageSizeDropdownList[0] 
      || null;

    // Ensure pageSize matches an available option
    if (!this.pageSizeOptions.includes(this.pageSize)) {
      this.pageSize = this.pageSizeOptions[0] || 8;
      this.pageSizeChange.emit(this.pageSize);
    }
  }

  private validateCurrentPage(): void {
    const maxPage = this.totalPages;
    if (this.currentPage > maxPage && maxPage > 0) {
      this.currentPage = 1;
      this.currentPageChange.emit(this.currentPage);
    }
  }

  /** Total number of pages */
  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalItems / this.pageSize));
  }

  /** Start index for slicing data (0-based) */
  get startIndex(): number {
    const validPage = Math.min(this.currentPage, this.totalPages);
    return (validPage - 1) * this.pageSize;
  }

  /** End index for slicing data */
  get endIndex(): number {
    return Math.min(this.startIndex + this.pageSize, this.totalItems);
  }

  /** Display string for items info (e.g., "1-8 of 266 items") */
  get itemsInfoText(): string {
    if (this.totalItems === 0) {
      return '0 items';
    }
    return `${this.startIndex + 1}-${this.endIndex} of ${this.totalItems} items`;
  }

  /** Handle page size selection from dropdown */
  onPageSizeSelect(event: any): void {
    const selected = Array.isArray(event?.selected) ? event.selected : (event?.selected ? [event.selected] : []);
    const change = selected[0] ?? null;
    
    if (change && change.value !== this.pageSize) {
      this.applyPageSizeChange(change);
    }
  }

  /** Handle dropdown item select event */
  onDropdownItemSelect(event: any): void {
    const selected = event?.selected;
    if (selected && selected.value !== this.pageSize) {
      this.applyPageSizeChange(selected);
    }
  }

  /** Apply page size change */
  private applyPageSizeChange(item: any): void {
    this.pageSize = item.value;
    this.selectedPageSizeItem = item;
    
    // Update selected state in list
    this.pageSizeDropdownList.forEach(listItem => {
      listItem.selected = listItem.value === this.pageSize;
    });
    
    // Reset to first page when page size changes
    this.currentPage = 1;
    
    this.pageSizeChange.emit(this.pageSize);
    this.currentPageChange.emit(this.currentPage);
    this.emitPaginationState();
  }

  /** Handle page change from ap-pagination */
  onPageChange(page: number): void {
    if (page !== this.currentPage && page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.currentPageChange.emit(this.currentPage);
      this.emitPaginationState();
    }
  }

  /** Handle Enter key in pagination input */
  onPaginationEnter(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input?.tagName === 'INPUT') {
      const value = parseInt(input.value, 10);
      if (!isNaN(value) && value >= 1 && value <= this.totalPages) {
        this.onPageChange(value);
      }
    }
  }

  /** Emit current pagination state */
  private emitPaginationState(): void {
    this.paginationChange.emit({
      startIndex: this.startIndex,
      endIndex: this.endIndex,
      pageSize: this.pageSize,
      currentPage: this.currentPage
    });
  }

  /** Helper for dropdown aria label */
  setAriaLabel(item: any): string {
    return `Page size option: ${item?.label || item?.value || '-'} items per page`;
  }
}
